/**
 * Tamano de la interfaz: kiosco grande o pantalla normal.
 *
 * Peticion de Ricardo (09/09/2026): "una opcion de interfaz con todo a tamano
 * normal, que ahora esta inviable". Y tiene razon: el kiosco esta calibrado
 * para un hombre de 88 anos delante de una tactil grande —texto base de 22px,
 * botones de 56px— y eso, en un portatil con raton, es una pared.
 *
 * Como funciona, y por que asi
 * ----------------------------
 * TODA la escala del kiosco esta en `rem` (ver `tailwind.config.js`), asi que
 * no hace falta tocar ni un componente: basta con mover el tamano de letra de
 * la raiz y el diseno entero se encoge o crece en proporcion —texto, huecos,
 * botones y radios a la vez—. Los puntos de ruptura de Tailwind estan en `px`,
 * de modo que el reparto de columnas NO cambia al reducir: solo el tamano.
 *
 * Es un ajuste POR DISPOSITIVO, guardado en el navegador, no en
 * `preferencias_ia.json`. Es deliberado: el kiosco del despacho y el portatil
 * de Ricardo abren la MISMA aplicacion con la misma cuenta. Si esto viajara al
 * servidor, encoger la pantalla aqui se la encogeria a su padre alli, que es
 * justo lo que no puede pasar.
 */

/** Los tres ajustes posibles. */
export type Escala = 'auto' | 'kiosco' | 'normal';

/** El tamano que de verdad se acaba pintando. */
export type EscalaEfectiva = 'kiosco' | 'normal';

/** Donde se recuerda la eleccion de ESTE navegador. */
const CLAVE_ALMACEN = 'androcles.escala';

/**
 * Tamano de letra de la raiz, en px, para cada modo.
 *
 * 16 es el valor de fabrica del navegador: con el, `text-base` son los 22px
 * del kiosco. 11.5 deja ese mismo `text-base` en ~16px, que es el texto normal
 * de cualquier pagina, y arrastra huecos y botones con el (los 56px del
 * objetivo tactil quedan en ~40, comodo con raton).
 */
const RAIZ_PX: Readonly<Record<EscalaEfectiva, number>> = {
  kiosco: 16,
  normal: 11.5,
};

/** Las opciones tal y como se le ensenan al usuario. */
export const ESCALAS: readonly { clave: Escala; etiqueta: string; ayuda: string }[] = [
  {
    clave: 'auto',
    etiqueta: 'Automático',
    ayuda: 'Grande en la pantalla táctil del despacho, normal en ordenador con ratón.',
  },
  {
    clave: 'kiosco',
    etiqueta: 'Grande (kiosco)',
    ayuda: 'Letra y botones grandes, pensado para leer y pulsar de lejos.',
  },
  {
    clave: 'normal',
    etiqueta: 'Normal (ordenador)',
    ayuda: 'Tamaño de una página corriente. Cabe mucho más en pantalla.',
  },
];

/** Lee la eleccion guardada; `auto` si no hay ninguna o el navegador no deja. */
export function leerEscala(): Escala {
  try {
    const valor = window.localStorage.getItem(CLAVE_ALMACEN);
    if (valor === 'kiosco' || valor === 'normal' || valor === 'auto') return valor;
  } catch {
    // Modo privado o almacenamiento bloqueado: no es motivo para no arrancar.
  }
  return 'auto';
}

/** Guarda la eleccion en ESTE navegador. Un fallo no rompe nada. */
export function guardarEscala(escala: Escala): void {
  try {
    window.localStorage.setItem(CLAVE_ALMACEN, escala);
  } catch {
    // Idem: se aplica igual en esta sesion, solo no se recuerda.
  }
}

/**
 * Que tamano toca de verdad.
 *
 * En `auto` decide el tipo de puntero, no el ancho: una tactil de despacho
 * puede ser enorme y aun asi necesitar botones grandes, y un portatil pequeno
 * con raton no los necesita. `pointer: coarse` es exactamente esa pregunta.
 */
export function escalaEfectiva(escala: Escala = leerEscala()): EscalaEfectiva {
  if (escala === 'kiosco' || escala === 'normal') return escala;
  try {
    return window.matchMedia('(pointer: coarse)').matches ? 'kiosco' : 'normal';
  } catch {
    // Sin matchMedia (entornos de prueba): el kiosco es el modo seguro, porque
    // quedarse corto de tamano es peor que sobrarle.
    return 'kiosco';
  }
}

/**
 * Aplica el tamano al documento.
 *
 * Se llama al arrancar (antes de pintar, para que no haya salto) y cada vez
 * que se cambia la opcion.
 */
export function aplicarEscala(escala: Escala = leerEscala()): EscalaEfectiva {
  const efectiva = escalaEfectiva(escala);
  const raiz = document.documentElement;
  raiz.style.fontSize = `${RAIZ_PX[efectiva]}px`;
  // Para que una hoja de estilos o un test puedan preguntar en que modo esta.
  raiz.dataset.escala = efectiva;
  return efectiva;
}
